import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Tag,
  Typography,
  Space,
  Divider,
} from "antd";
import { LinkOutlined } from "@ant-design/icons";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { DeviceType } from "../../../../configs/constants";

const { Text, Link } = Typography;

const URLS = {
  [DeviceType.Kds]: `${window.location.origin}/kds`,
  [DeviceType.Cds]: `${window.location.origin}/cds`,
};

const PairingCodeModal = ({
  open,
  title,
  onCancel,
  form,
  branchesData = [],
  dataLoading = false,
  apiResponse,
  remainingSeconds = 0,
  onGenerate,
  onResetPairing,
  showLinkedPosUid = false,
  posDeviceUid = "",
  notRegisteredText = "",
  deviceType = "",
}) => {
  const { t } = useTranslation();

  const isExpired = !!apiResponse && remainingSeconds === 0;

  const otp = String(apiResponse?.pairing_code || "").padStart(6, "0");
  const portalUrl = URLS[deviceType] || URLS[DeviceType.Kds];

  const renderOtpBoxes = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        gap: 10,
        marginTop: 10,
      }}
    >
      {otp.split("").map((digit, index) => (
        <div
          key={index}
          style={{
            width: 52,
            height: 64,
            border: "2px solid var(--color-success)",
            borderRadius: 12,
            fontSize: 30,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background:
              "linear-gradient(180deg, var(--color-success-bg) 0%, var(--color-white) 100%)",
            boxShadow: "0 6px 16px var(--color-shadow-soft)",
            userSelect: "none",
          }}
        >
          {digit}
        </div>
      ))}
    </div>
  );

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onCancel}
      footer={null}
      maskClosable={false}
    >
      {apiResponse ? (
        <div style={{ marginBottom: 10 }}>
          {!isExpired ? (
            <>
              <div
                style={{
                  fontSize: 13,
                  marginBottom: 10,
                  color: "var(--color-text)",
                }}
              >
                {t("enter_pairing_code_instruction")}{" "}
                <strong>
                  {moment.utc(remainingSeconds * 1000).format("mm:ss")}
                </strong>
              </div>

              {renderOtpBoxes()}

              <Divider style={{ margin: "16px 0 10px" }} />

              <div
                style={{
                  padding: 12,
                  borderRadius: 12,
                  border: "1px solid var(--color-bg)",
                  background: "var(--color-bg)",
                }}
              >
                <Space direction="vertical" size={6} className="app-w-full">
                  <Text type="secondary" className="app-text-xs">
                    <LinkOutlined /> {t("open_screen_device_url_instruction")}
                  </Text>

                  <Link href={portalUrl} target="_blank" rel="noreferrer">
                    {portalUrl}
                  </Link>
                </Space>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  marginTop: 20,
                }}
              >
                <Button type="default" onClick={onCancel}>
                  {t("cancel")}
                </Button>
              </div>
            </>
          ) : (
            <div style={{ textAlign: "center", paddingTop: 6 }}>
              <Tag color="error" style={{ fontSize: 14 }}>
                {t("expired")}
              </Tag>

              <div style={{ marginTop: 8, color: "var(--color-text-muted)" }}>
                {t("pairing_code_expired_message")}
              </div>

              <div style={{ marginTop: 12 }}>
                <Button
                  type="primary"
                  loading={dataLoading}
                  onClick={onResetPairing}
                >
                  {t("generate_code")}
                </Button>
              </div>

              <div style={{ marginTop: 12 }}>
                <Button type="default" onClick={onCancel}>
                  {t("cancel")}
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <Form form={form} layout="vertical">
            <Form.Item
              name="device_name"
              label={t("name")}
              rules={[{ whitespace: true }]}
              hasFeedback
            >
              <Input placeholder={t("name")} />
            </Form.Item>

            <Form.Item
              name="branch_id"
              label={t("branch")}
              rules={[
                { required: true, message: t("validate_branch_required") },
              ]}
              hasFeedback
            >
              <Select
                showSearch
                allowClear
                placeholder={t("branch")}
                options={branchesData.map((b) => ({
                  label: b.name,
                  value: b.id,
                }))}
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>

            {showLinkedPosUid ? (
              <div style={{ fontSize: 12, opacity: 0.75 }}>
                {t("linked_pos_uid")}:{" "}
                <Tag color={posDeviceUid ? "green" : "red"}>
                  {posDeviceUid || notRegisteredText || t("not_registered")}
                </Tag>
              </div>
            ) : null}
          </Form>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 20,
            }}
          >
            <Button type="default" onClick={onCancel}>
              {t("cancel")}
            </Button>
            <Button type="primary" loading={dataLoading} onClick={onGenerate}>
              {t("generate_code")}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default PairingCodeModal;
